import { ListBox, ListBoxRow, Pressable } from '@spotify-internal/encore-web';
import { Popover, PopoverRoot } from '@spotify-internal/encore-web/next';
import { ObservationsV1IssueState as IssueState } from '@spotify/confidence-graphql';

import {
  ISSUE_CLOSE_REASONS,
  IssueCloseReason,
  getIssueCloseReasonId,
  getIssueCloseReasonLabel,
  getIssueStateLabel,
} from '../../types';
import { IssueStateIcon, type IssueStateIconProps } from '../IssueStateIcon';

import styles from './IssueStatePicker.module.css';

const IssueStateAction = {
  Start: 'start',
  Reopen: 'reopen',
} as const;

type IssueStatePickerOptionsProps = Omit<
  IssueStatePickerProps,
  'canEdit' | 'isAgentRunning' | 'isLoading' | 'isSnoozed'
> & {
  close: () => void;
};

export type IssueStatePickerProps = {
  canEdit: boolean;
  closeReason?: IssueCloseReason | null;
  disableIcon?: boolean;
  isAgentRunning?: boolean;
  isLoading?: boolean;
  isSnoozed?: boolean;
  iconProps?: IssueStateIconProps['iconProps'];
  state: IssueState;
  onClose: (closeReason: IssueCloseReason) => void;
  onReopen: () => void;
  onStart: () => void;
};

type IssueStateOptionProps = {
  disabled?: boolean;
  id: string;
  label: string;
  closeReason?: IssueCloseReason | null;
  state: IssueState;
};

const IssueStateOption = ({
  disabled = false,
  id,
  label,
  closeReason,
  state,
}: IssueStateOptionProps) => (
  <ListBoxRow id={id} textValue={label} size="small" isDisabled={disabled}>
    <IssueStateIcon
      closeReason={closeReason}
      state={state}
      label={label}
      typographyProps={{ variant: 'body2' }}
      iconProps={{ fontSize: 'small' }}
    />
  </ListBoxRow>
);

const IssueStatePickerOptions = ({
  close,
  closeReason,
  onClose,
  onReopen,
  onStart,
  state,
}: IssueStatePickerOptionsProps) => {
  const handleSelectionChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') {
      return;
    }

    const [action] = keys;
    const selectedCloseReason = ISSUE_CLOSE_REASONS.find(
      reason => getIssueCloseReasonId(reason) === action,
    );

    if (selectedCloseReason) {
      onClose(selectedCloseReason);
    } else if (action === IssueStateAction.Start) {
      onStart();
    } else if (action === IssueStateAction.Reopen) {
      onReopen();
    } else {
      return;
    }

    close();
  };

  return (
    <ListBox
      aria-label="Change issue state"
      selectionMode="single"
      onSelectionChange={handleSelectionChange}
    >
      <IssueStateOption
        disabled={state !== IssueState.Closed}
        id={IssueStateAction.Reopen}
        label="Waiting"
        state={IssueState.Waiting}
      />
      <IssueStateOption
        disabled={state !== IssueState.Waiting}
        id={IssueStateAction.Start}
        label="Working"
        state={IssueState.Working}
      />
      {ISSUE_CLOSE_REASONS.map(reason => (
        <IssueStateOption
          key={reason}
          disabled={state === IssueState.Closed && closeReason === reason}
          id={getIssueCloseReasonId(reason)}
          label={getIssueCloseReasonLabel(reason)}
          closeReason={reason}
          state={IssueState.Closed}
        />
      ))}
    </ListBox>
  );
};

export const IssueStatePicker = ({
  canEdit,
  closeReason,
  disableIcon = false,
  isAgentRunning = false,
  isLoading = false,
  isSnoozed = false,
  iconProps,
  state,
  onClose,
  onReopen,
  onStart,
}: IssueStatePickerProps) => {
  const isDisabled = !canEdit || isLoading || isSnoozed;
  const stateLabel =
    state === IssueState.Closed
      ? getIssueCloseReasonLabel(closeReason)
      : getIssueStateLabel(state);
  const label = isSnoozed ? 'Snoozed' : stateLabel;
  const trigger = (
    <button
      type="button"
      className={styles.trigger}
      disabled={isDisabled}
      aria-label={`Change issue state, current value: ${label}`}
     data-track-source-anchor="7ea3a04723;issue-state-picker-button">
      <IssueStateIcon
        aria-label={isAgentRunning ? 'Agent running' : undefined}
        closeReason={closeReason}
        disableIcon={disableIcon}
        isAgentRunning={isAgentRunning}
        isSnoozed={isSnoozed}
        state={state}
        label={label}
        typographyProps={{ variant: 'body2' }}
        iconProps={{ fontSize: 'small', ...iconProps }}
      />
    </button>
  );

  if (isDisabled) {
    return trigger;
  }

  return (
    <PopoverRoot>
      <Pressable>{trigger}</Pressable>
      <Popover
        aria-label="Change issue state"
        placement="bottomStart"
        hasNoArrow
        className={styles.popover}
      >
        {({ close }) => (
          <IssueStatePickerOptions
            state={state}
            closeReason={closeReason}
            onStart={onStart}
            onClose={onClose}
            onReopen={onReopen}
            close={close}
          />
        )}
      </Popover>
    </PopoverRoot>
  );
};
